import httpService from '@Utils/httpService';
import { log as debugLog, error as debugError } from '@/utils/debugLogger';

/**
 * SAS Token Cache Service
 *
 * Fetches and caches Azure Blob Storage read SAS tokens from the backend.
 * The token is cached in memory and refreshed when it approaches expiry.
 *
 * Used by documentProxyUtils.getDocumentBlobUrl() to construct direct blob URLs.
 */

let cachedToken = null;
let tokenExpiry = null;

const REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry

/**
 * Parse the expiry time from a SAS query string.
 * Looks for the `se=` parameter (SignedExpiry).
 *
 * @param {string} sasToken - SAS query string (e.g. "?sv=2021-06-08&se=2026-04-12T14:00:00Z&sig=...")
 * @returns {Date|null} - Expiry date or null if not parseable
 */
function parseExpiry(sasToken) {
  try {
    const params = new URLSearchParams(sasToken.replace(/^\?/, ''));
    const se = params.get('se');
    if (!se) return null;
    const date = new Date(se);
    return isNaN(date.getTime()) ? null : date;
  } catch (err) {
    debugError('[SasTokenCache]', 'Failed to parse SAS expiry:', err);
    return null;
  }
}

/**
 * Check if the cached token is still valid (not expired or within refresh buffer).
 * @returns {boolean}
 */
function isCacheValid() {
  if (!cachedToken || !tokenExpiry) return false;
  return Date.now() < tokenExpiry.getTime() - REFRESH_BUFFER_MS;
}

/**
 * Fetch a fresh read SAS token from the backend and cache it.
 * Returns the cached token if still valid.
 *
 * @returns {Promise<string>} - SAS query string (e.g. "?sv=...&se=...&sig=...")
 */
export async function getSasToken() {
  if (isCacheValid()) {
    return cachedToken;
  }

  debugLog('[SasTokenCache]', 'Fetching fresh SAS token');

  const response = await httpService.get({
    url: 'api/documents/sas-token',
  });

  let token = response.data;

  // Ensure token starts with '?'
  if (token && !token.startsWith('?')) {
    token = '?' + token;
  }

  cachedToken = token;
  tokenExpiry = parseExpiry(token);

  if (!tokenExpiry) {
    // If we can't parse expiry, cache for 30 minutes as a safe default
    tokenExpiry = new Date(Date.now() + 30 * 60 * 1000);
    debugLog('[SasTokenCache]', 'Could not parse SAS expiry, using 30min default');
  }

  debugLog('[SasTokenCache]', 'Token cached, expires:', tokenExpiry.toISOString());

  return cachedToken;
}

/**
 * Clear the cached SAS token. Call on logout to prevent stale tokens.
 */
export function clearSasCache() {
  cachedToken = null;
  tokenExpiry = null;
  debugLog('[SasTokenCache]', 'Cache cleared');
}
